// The search at the top of the home page (Oct 2026, user: "we have a new
// tunes page for the search which … could be folded into the home page with a
// search bar at the top and all the sections below"). It began as its own
// page (#455) and keeps everything that page did: one search over every
// edition by title or composer, the melody and personal filters, version, key
// and style refinements, playlist actions beside each result, and its state in
// the URL, so Back from a tune restores the search and its scroll position.
// Typing, a filter or Browse all shows results in place of the home sections
// (`children`); clearing the search brings them back.
//
// A result says what it IS — a score, a lead sheet, a chord chart, your own
// copy — never the collection it came from (user ruling, Oct 2026). The
// catalogues load on the first sign of a search, so a visit that never
// searches never fetches them.

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom';
import { ChevronRight, Music2, Search, SlidersHorizontal, Upload, X } from 'lucide-react';
import { APP_ROUTES } from '@/navigation';
import { useTuneSearchSources } from '@/data/useTuneSearchSources';
import { searchTuneRows, tuneSearchKeyLabel } from '@/data/tuneSearch';
import { tuneVersionLabel, type TuneSourceEntry, type TuneSourceKind } from '@/data/tuneSources';
import { usePlaylistsAvailable } from '@/data/playlistAccess';
import { AddTuneToPlaylist } from '@/components/AddTuneToPlaylist';
import { ImportPanel } from '@/components/ImportPanel';
import { PlaylistScoreUpdate } from '@/components/PlaylistScoreUpdate';
import { useDialogInteraction } from '@/components/useDialogInteraction';
import './TuneSearch.css';

const PAGE_SIZE = 40;
/** Where a page was left: the scroll, and on the home page the section or
 * card at the top of the screen with how far down the screen it sat. */
interface ScrollMark {
  y: number;
  anchor?: string;
  top?: number;
}
const scrollPositions = new Map<string, ScrollMark>();

/** The topmost section or card on screen (`data-scroll-anchor`) and where it
 * sits, so a return can put it back there even if something above it changed:
 * opening a tune adds a More like shelf above the library. */
function scrollMark(): ScrollMark {
  const y = window.scrollY;
  for (const el of document.querySelectorAll<HTMLElement>('[data-scroll-anchor]')) {
    const { top, bottom } = el.getBoundingClientRect();
    if (bottom > 0) return { y, anchor: el.dataset.scrollAnchor, top };
  }
  return { y };
}

/** What kind of version a result is, the Version filter's vocabulary. */
type Version =
  | 'score'
  | 'lead'
  | 'chart'
  | 'abc'
  | 'saved'
  | 'shared'
  | 'library'
  | 'playlist'
  | 'study';
const VERSION_OF: Record<TuneSourceKind, Version> = {
  native: 'score',
  realbook: 'lead',
  openbook: 'lead',
  tune: 'chart',
  abc: 'abc',
  saved: 'saved',
  shared: 'shared',
  library: 'library',
  playlist: 'playlist',
  preset: 'study',
};
const VERSION_NAMES: Record<Version, string> = {
  score: 'Scores',
  lead: 'Lead sheets',
  chart: 'Chord charts',
  abc: 'ABC',
  saved: 'Saved tunes',
  shared: 'Shared tunes',
  library: 'Library',
  playlist: 'My playlists',
  study: 'Studies',
};
const VERSION_ORDER = Object.keys(VERSION_NAMES) as Version[];
const isVersion = (value: string | null): value is Version =>
  !!value && (VERSION_ORDER as string[]).includes(value);

/**
 * Put the page back where it was left: its marked section or card where it
 * sat on screen, else the scroll. The home sections arrive a moment after the
 * page does (recents wait on the manifests) and can change height as they do,
 * so a marked place is held for a second and a half, and a bare scroll is
 * retried until the page is tall enough to reach it. Either stops as soon as
 * the reader scrolls themselves.
 */
function restoreScroll(mark: ScrollMark | undefined): void {
  const where = (): number | null => {
    if (!mark) return 0;
    if (!mark.anchor) return mark.y;
    // Compared, not selected by value: a shelf's key carries a tune's title.
    const el = [...document.querySelectorAll<HTMLElement>('[data-scroll-anchor]')].find(
      (candidate) => candidate.dataset.scrollAnchor === mark.anchor,
    );
    return el ? window.scrollY + el.getBoundingClientRect().top - (mark.top ?? 0) : null;
  };
  window.scrollTo(0, where() ?? mark?.y ?? 0);
  if (!mark || (mark.y <= 0 && !mark.anchor)) return;
  // Not tied to the effect's cleanup (StrictMode runs that at once in
  // development): the loop ends itself, and on leaving the page.
  const href = window.location.href;
  const started = performance.now();
  let frame = 0;
  const stop = () => {
    cancelAnimationFrame(frame);
    window.removeEventListener('wheel', stop);
    window.removeEventListener('touchstart', stop);
    window.removeEventListener('keydown', stop);
  };
  const step = () => {
    const target = where();
    if (
      window.location.href !== href ||
      performance.now() - started > 1500 ||
      (!mark.anchor && target !== null && Math.abs(window.scrollY - target) <= 2)
    ) {
      stop();
      return;
    }
    window.scrollTo(0, target ?? mark.y);
    frame = requestAnimationFrame(step);
  };
  window.addEventListener('wheel', stop, { passive: true });
  window.addEventListener('touchstart', stop, { passive: true });
  window.addEventListener('keydown', stop);
  frame = requestAnimationFrame(step);
}

/** Keep an import's parsing, warnings and playlist tools on the established path. */
function ImportDialog({ onClose, onImported }: { onClose: () => void; onImported: () => void }) {
  const dialog = useDialogInteraction<HTMLDivElement>(true, onClose);
  const pressedOnBackdrop = useRef(false);
  return (
    <div
      className="modal-overlay tunes-import-overlay"
      role="presentation"
      onPointerDown={(e) => {
        pressedOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (pressedOnBackdrop.current && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialog}
        className="tunes-import-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Import tunes"
        tabIndex={-1}
      >
        <ImportPanel onClose={onClose} onImported={onImported} importFirst />
      </div>
    </div>
  );
}

export function TuneSearch({ children }: { children: ReactNode }) {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const playlistsAvailable = usePlaylistsAvailable();
  const [showImport, setShowImport] = useState(false);
  const [opening, setOpening] = useState<string | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const openingRef = useRef(false);
  const searchInput = useRef<HTMLInputElement>(null);
  const query = params.get('q') ?? '';
  const filter =
    params.get('filter') === 'melody' ? 'melody' : params.get('filter') === 'mine' ? 'mine' : 'all';
  // `source` is the Tunes page's old collection parameter: a link that still
  // carries it opens on the matching version.
  const legacySource = params.get('source') as TuneSourceKind | null;
  const version: Version | '' = isVersion(params.get('version'))
    ? (params.get('version') as Version)
    : legacySource && Object.hasOwn(VERSION_OF, legacySource)
      ? VERSION_OF[legacySource]
      : '';
  const musicKey = tuneSearchKeyLabel(params.get('key') ?? '');
  const style = params.get('style') ?? '';
  const browsing = !!(
    query.trim() ||
    version ||
    musicKey ||
    style ||
    filter !== 'all' ||
    params.has('browse')
  );
  const { rows, loading, error, retry } = useTuneSearchSources(browsing || touched);
  const requestedCount = Number(params.get('count'));
  const visibleCount = Number.isFinite(requestedCount)
    ? Math.max(PAGE_SIZE, Math.floor(requestedCount))
    : PAGE_SIZE;
  const [showFilters, setShowFilters] = useState(!!(version || musicKey || style));

  // Entering a search from the home sections is a step Back can undo; every
  // change after that (each keystroke) replaces it.
  const update = (values: Record<string, string>, replace = browsing) => {
    const next = new URLSearchParams(params);
    next.delete('count');
    // An old link's collection becomes its version the first time anything changes.
    if (next.has('source')) {
      if (version && !next.has('version')) next.set('version', version);
      next.delete('source');
    }
    for (const [key, value] of Object.entries(values)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setParams(next, { replace });
    setOpenError(null);
  };

  const versionOptions = useMemo(
    () => VERSION_ORDER.filter((value) => rows.some((row) => VERSION_OF[row.source] === value)),
    [rows],
  );
  const keyOptions = useMemo(
    () =>
      Array.from(
        new Set(rows.flatMap((row) => (row.musicKey ? [tuneSearchKeyLabel(row.musicKey)] : []))),
      ).sort(),
    [rows],
  );
  const styleOptions = useMemo(
    () => Array.from(new Set(rows.flatMap((row) => (row.style ? [row.style] : [])))).sort(),
    [rows],
  );
  const matches = useMemo(
    () =>
      searchTuneRows(
        rows.filter(
          (row) =>
            (filter !== 'melody' || row.melody) &&
            (filter !== 'mine' || ['saved', 'playlist', 'shared', 'abc'].includes(row.source)) &&
            (!version || VERSION_OF[row.source] === version) &&
            (!musicKey || tuneSearchKeyLabel(row.musicKey ?? '') === musicKey) &&
            (!style || row.style === style),
        ),
        query,
      ),
    [rows, query, filter, version, musicKey, style],
  );
  const shown = browsing ? matches.slice(0, visibleCount) : [];

  // Search lives in the URL; returning from a tune also restores the page's
  // scroll, a long result list's or the home sections'.
  const scrollKey = location.pathname + location.search;
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current || (browsing && loading)) return;
    restored.current = true;
    restoreScroll(navigationType === 'POP' ? scrollPositions.get(scrollKey) : undefined);
  }, [browsing, loading, navigationType, scrollKey]);
  useEffect(() => {
    const remember = () => {
      scrollPositions.set(scrollKey, scrollMark());
      if (scrollPositions.size > 40) scrollPositions.delete(scrollPositions.keys().next().value!);
    };
    window.addEventListener('scroll', remember, { passive: true });
    return () => window.removeEventListener('scroll', remember);
  }, [scrollKey]);

  const open = async (row: TuneSourceEntry) => {
    if (openingRef.current) return;
    // Showing more changes the URL without necessarily causing a scroll event.
    scrollPositions.set(scrollKey, scrollMark());
    openingRef.current = true;
    setOpening(row.key);
    setOpenError(null);
    try {
      await row.load();
      navigate(APP_ROUTES.workbench);
    } catch (cause) {
      setOpenError(
        cause instanceof Error ? cause.message : 'Could not open that tune. Please try again.',
      );
    } finally {
      openingRef.current = false;
      setOpening(null);
    }
  };

  return (
    <>
      <div className="tune-search" role="search" data-testid="tune-search">
        <div className="tune-search-field">
          <Search size={23} strokeWidth={1.6} aria-hidden="true" />
          <input
            ref={searchInput}
            type="search"
            aria-label="Search title or composer"
            placeholder="Search title or composer…"
            value={query}
            onFocus={() => setTouched(true)}
            onChange={(e) => update({ q: e.target.value })}
            autoComplete="off"
            spellCheck={false}
          />
          {query && (
            <button
              type="button"
              className="tune-search-clear"
              aria-label="Clear search"
              onClick={() => {
                update({ q: '' });
                searchInput.current?.focus();
              }}
            >
              <X size={18} aria-hidden="true" />
            </button>
          )}
        </div>
        <div className="tune-search-filters">
          <div
            className="tune-search-chips"
            role="group"
            aria-label="Show tunes"
            data-idle={browsing ? undefined : ''}
          >
            {(
              [
                ['all', 'All tunes'],
                ['melody', 'With melody'],
                ['mine', 'My tunes'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`tune-search-chip${browsing && filter === value ? ' selected' : ''}`}
                aria-pressed={browsing && filter === value}
                onClick={() =>
                  update({
                    filter: value === 'all' ? '' : value,
                    browse: value === 'all' ? 'all' : '',
                    version: '',
                  })
                }
              >
                {label}
              </button>
            ))}
          </div>
          <div className="tune-search-tools">
            <button
              type="button"
              className="tune-search-refine"
              aria-expanded={showFilters}
              aria-controls="tune-search-more-filters"
              onClick={() => {
                setTouched(true);
                setShowFilters((v) => !v);
              }}
            >
              <SlidersHorizontal size={15} aria-hidden="true" /> Filters
              {version || musicKey || style ? ' · on' : ''}
            </button>
            <button
              type="button"
              className="tune-search-refine"
              data-testid="import-tunes"
              onClick={() => setShowImport(true)}
            >
              <Upload size={15} aria-hidden="true" /> Import
            </button>
          </div>
        </div>
        {showFilters && (
          <div className="tune-search-more-filters" id="tune-search-more-filters">
            <label>
              Version
              <select value={version} onChange={(e) => update({ version: e.target.value })}>
                <option value="">Every version</option>
                {versionOptions.map((value) => (
                  <option key={value} value={value}>
                    {VERSION_NAMES[value]}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Key
              <select value={musicKey} onChange={(e) => update({ key: e.target.value })}>
                <option value="">Any key</option>
                {keyOptions.map((value) => (
                  <option key={value}>{value}</option>
                ))}
              </select>
            </label>
            <label>
              Style
              <select value={style} onChange={(e) => update({ style: e.target.value })}>
                <option value="">Any style</option>
                {styleOptions.map((value) => (
                  <option key={value}>{value}</option>
                ))}
              </select>
            </label>
            {(version || musicKey || style) && (
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={() => update({ version: '', key: '', style: '' })}
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {browsing ? (
        <>
          {error && (
            <div className="tune-search-notice" role="alert">
              <span>{error}</span>
              <button type="button" className="btn btn-sm" onClick={retry}>
                Try again
              </button>
            </div>
          )}
          {openError && (
            <p className="tune-search-error" role="alert">
              {openError}
            </p>
          )}
          <section
            className="tune-search-results"
            aria-labelledby="tune-search-results-title"
            aria-busy={loading || !!opening}
          >
            <div className="tune-search-results-head">
              <h2 id="tune-search-results-title">
                {query.trim()
                  ? 'Search results'
                  : version
                    ? VERSION_NAMES[version]
                    : filter === 'mine'
                      ? 'My tunes'
                      : filter === 'melody'
                        ? 'Tunes with melody'
                        : 'All tunes'}
              </h2>
              <span role="status">
                {loading
                  ? 'Loading tunes…'
                  : `${matches.length.toLocaleString()} ${matches.length === 1 ? 'tune' : 'tunes'}`}
              </span>
              <button
                type="button"
                className="tune-search-refine"
                data-testid="tune-search-reset"
                onClick={() => setParams({}, { replace: true })}
              >
                <X size={15} aria-hidden="true" /> Clear
              </button>
            </div>
            <ul className="tune-search-list">
              {shown.map((row) => (
                <li key={row.key} className="tune-search-row">
                  <div className="tune-search-row-main">
                    <button
                      type="button"
                      className="tune-search-open"
                      aria-label={`Open ${row.title}${row.context ? ` · ${row.context}` : ''}`}
                      disabled={!!opening}
                      onClick={() => void open(row)}
                    >
                      <span className="tune-search-row-copy">
                        <span className="tune-search-title">{row.title}</span>
                        <span className="tune-search-composer">
                          {row.composer || 'Composer not listed'}
                        </span>
                        <span className="tune-search-origin">
                          {[tuneVersionLabel(row.source), row.context, row.musicKey, row.note]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </span>
                      <span className={`tune-search-badge${row.melody ? ' has-melody' : ''}`}>
                        {row.melody && <Music2 size={12} aria-hidden="true" />}
                        {row.melody ? 'Melody' : 'Chords'}
                      </span>
                      <span className="tune-search-row-arrow">
                        {opening === row.key ? (
                          '…'
                        ) : (
                          <ChevronRight size={18} strokeWidth={1.5} aria-hidden="true" />
                        )}
                      </span>
                    </button>
                    {playlistsAvailable && row.ref && (
                      <div className="tune-search-add">
                        <AddTuneToPlaylist tuneRef={row.ref} title={row.title} compact />
                      </div>
                    )}
                  </div>
                  <PlaylistScoreUpdate row={row} onUpdated={() => setOpenError(null)} />
                </li>
              ))}
            </ul>
            {!loading && shown.length === 0 && (
              <div className="tune-search-empty">
                <h3>
                  {filter === 'mine' && !query ? 'Your tunes will appear here' : 'No tunes found'}
                </h3>
                <p>
                  {filter === 'mine' && !query
                    ? 'Saved tunes and the tunes in your playlists are collected here.'
                    : 'Try a shorter title, a composer’s name, or fewer filters.'}
                </p>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => setParams({}, { replace: true })}
                >
                  Reset search
                </button>
              </div>
            )}
            {shown.length < matches.length && (
              <button
                type="button"
                className="btn tune-search-more"
                onClick={() => update({ count: String(visibleCount + PAGE_SIZE) })}
              >
                Show more tunes{' '}
                <span className="muted">
                  ({shown.length} of {matches.length.toLocaleString()})
                </span>
              </button>
            )}
          </section>
        </>
      ) : (
        children
      )}
      {showImport && (
        <ImportDialog
          onClose={() => setShowImport(false)}
          onImported={() => navigate(APP_ROUTES.workbench)}
        />
      )}
    </>
  );
}
